import { useState, useEffect, useCallback, useRef } from 'react';
import { kpisAPI } from './api';

/**
 * Custom hook for managing KPI order with drag & drop support.
 * 
 * Implements the merge algorithm:
 * 1. Get visible KPIs (from props)
 * 2. Filter ordered_kpi_ids to only include visible KPIs
 * 3. Add any new/missing visible KPIs at the end
 * 4. Return the merged, ordered list
 */
export function useKPIOrder(visibleKpis = []) {
  const [savedOrder, setSavedOrder] = useState([]);
  const [orderedKpis, setOrderedKpis] = useState([]);
  const [isManualOrder, setIsManualOrder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const prevVisibleIdsRef = useRef('');

  // Load saved order on mount
  useEffect(() => {
    const loadOrder = async () => {
      try {
        const response = await kpisAPI.getOrder();
        const orderData = response.data;
        setSavedOrder(orderData.ordered_kpi_ids || []);
        setIsManualOrder((orderData.ordered_kpi_ids || []).length > 0);
      } catch (error) {
        console.error('Failed to load KPI order:', error);
        setSavedOrder([]);
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, []);

  // Apply merge algorithm when visibleKpis or savedOrder changes
  useEffect(() => {
    if (loading) return;
    
    // Content-aware signature: update rows when KPI DATA changes (not only when the
    // set of ids changes), but stay stable when the parent passes a fresh empty array
    // reference on each render (avoids an infinite setState loop).
    const contentSig = JSON.stringify({
      k: visibleKpis.map(kpi => [kpi.id, kpi.total_result, kpi.total_goal, kpi.status, kpi.performance, kpi.captured_periods]),
      o: savedOrder,
      m: isManualOrder,
    });
    if (contentSig === prevVisibleIdsRef.current) {
      return; // No relevant change
    }
    prevVisibleIdsRef.current = contentSig;
    
    if (visibleKpis.length === 0) {
      setOrderedKpis([]);
      return;
    }
    
    const visibleIdSet = new Set(visibleKpis.map(kpi => kpi.id));
    
    // Filter saved order to only include visible KPIs
    const orderedVisible = savedOrder.filter(id => visibleIdSet.has(id));
    
    // Find KPIs not in saved order (new or missing)
    const orderedSet = new Set(orderedVisible);
    const missing = visibleKpis.filter(kpi => !orderedSet.has(kpi.id));
    
    // Build final ordered list: ordered first, then missing at end
    const orderedKpiMap = new Map(visibleKpis.map(kpi => [kpi.id, kpi]));
    const finalList = [
      ...orderedVisible.map(id => orderedKpiMap.get(id)).filter(Boolean),
      ...missing
    ];
    
    if (isManualOrder && savedOrder.length > 0) {
      setOrderedKpis(finalList);
    } else {
      // Default order (as received from backend)
      setOrderedKpis(visibleKpis);
    }
  }, [visibleKpis, savedOrder, loading, isManualOrder]);

  // Save order to backend
  const saveOrder = useCallback(async (newOrderedKpis) => {
    setSaving(true);
    try {
      const orderedIds = newOrderedKpis.map(kpi => kpi.id);
      await kpisAPI.updateOrder(orderedIds);
      setSavedOrder(orderedIds);
      setIsManualOrder(true);
    } catch (error) {
      console.error('Failed to save KPI order:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, []);

  // Handle drag end - reorder and save
  const handleDragEnd = useCallback(async (activeId, overId) => {
    if (!activeId || !overId || activeId === overId) return;
    
    const oldIndex = orderedKpis.findIndex(kpi => kpi.id === activeId);
    const newIndex = orderedKpis.findIndex(kpi => kpi.id === overId);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Create new array with reordered items
    const newOrderedKpis = [...orderedKpis];
    const [removed] = newOrderedKpis.splice(oldIndex, 1);
    newOrderedKpis.splice(newIndex, 0, removed);
    
    // Update local state immediately for responsive UI
    setOrderedKpis(newOrderedKpis);
    
    // Save to backend
    try {
      await saveOrder(newOrderedKpis);
    } catch (error) {
      // Don't revert on error - let the UI stay updated
      console.error('Failed to save order:', error);
    }
  }, [orderedKpis, saveOrder]);

  // Move KPI up or down (for mobile fallback)
  const moveKpi = useCallback(async (kpiId, direction) => {
    const currentIndex = orderedKpis.findIndex(kpi => kpi.id === kpiId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= orderedKpis.length) return;
    
    const newOrderedKpis = [...orderedKpis];
    const [removed] = newOrderedKpis.splice(currentIndex, 1);
    newOrderedKpis.splice(newIndex, 0, removed);
    
    setOrderedKpis(newOrderedKpis);
    
    try {
      await saveOrder(newOrderedKpis);
    } catch (error) {
      console.error('Failed to save order:', error);
    }
  }, [orderedKpis, saveOrder]);

  // Reset to default order
  const resetOrder = useCallback(async () => {
    setSaving(true);
    try {
      await kpisAPI.resetOrder();
      setSavedOrder([]);
      setIsManualOrder(false);
      setOrderedKpis(visibleKpis); // Return to default order
    } catch (error) {
      console.error('Failed to reset KPI order:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [visibleKpis]);

  return {
    orderedKpis,
    loading,
    saving,
    isManualOrder,
    handleDragEnd,
    moveKpi,
    resetOrder,
    saveOrder,
    setIsManualOrder,
  };
}

export default useKPIOrder;
